import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronRight, Trash2 } from 'lucide-react';

import {
  useMyAccountsQuery,
  useUnlinkRiotAccountMutation,
} from '@/services/qriotsApi';
import type { IdentityGame } from '@/types/qriots';
import { classifyQriotsError } from '@/services/qriotsErrors';
import { Loader } from '@/components/common/Loader';
import { QriotsErrorState } from '@/components/riot/QriotsErrorState';

const GAME_EMOJI: Record<IdentityGame, string> = {
  lol: '🎯',
  tft: '♟',
  lor: '🃏',
  valorant: '🔫',
};

function formatLinkedAt(iso: string): string {
  const diffMs = Date.now() - Date.parse(iso);
  const h = Math.floor(diffMs / 1000 / 3600);
  if (h < 1) return 'just now';
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export function RiotSaved() {
  const { t } = useTranslation('riot');
  const result = useMyAccountsQuery();
  const [unlink, unlinkResult] = useUnlinkRiotAccountMutation();

  if (result.isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader />
      </div>
    );
  }

  if (result.error) {
    const { kind, retryAfterSec } = classifyQriotsError(result.error);
    return (
      <QriotsErrorState
        kind={kind}
        retryAfterSec={retryAfterSec}
        onRetry={result.refetch}
      />
    );
  }

  const accounts = result.data?.accounts ?? [];

  return (
    <div className="flex flex-col gap-4 pb-6 pt-4">
      <div className="mx-4 flex items-baseline justify-between">
        <h2 className="text-base font-bold text-fg">{t('saved.title')}</h2>
        <span className="text-xs text-fg-muted">
          {t('saved.count', { count: accounts.length })}
        </span>
      </div>

      {accounts.length === 0 ? (
        <div className="mx-4 rounded-xl bg-bg-secondary p-6 text-center">
          <p className="text-sm text-fg-muted">{t('saved.empty')}</p>
          <Link
            to="/riot"
            className="mt-3 inline-block rounded-lg bg-success/15 px-4 py-2 text-sm font-medium text-fg active:opacity-80"
          >
            {t('cta.checkProfile')}
          </Link>
        </div>
      ) : (
        <ul className="mx-4 flex flex-col gap-2">
          {accounts.map((a) => {
            const detailHref =
              a.game === 'lol'
                ? `/riot/lol/${encodeURIComponent(a.puuid)}?region=${a.region}&gameName=${encodeURIComponent(a.riotId.gameName)}&tagLine=${encodeURIComponent(a.riotId.tagLine)}`
                : a.game === 'tft'
                  ? `/riot/tft/${encodeURIComponent(a.puuid)}?region=${a.region}&gameName=${encodeURIComponent(a.riotId.gameName)}&tagLine=${encodeURIComponent(a.riotId.tagLine)}`
                  : null;

            return (
              <li
                key={`${a.puuid}-${a.game}`}
                className="flex items-center gap-3 rounded-lg border border-border bg-bg-tertiary px-3 py-2.5"
              >
                <span aria-hidden className="text-xl leading-none">
                  {GAME_EMOJI[a.game] ?? '🎮'}
                </span>
                <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                  <p className="truncate text-sm font-bold text-fg">
                    {a.riotId.gameName}
                    <span className="text-fg-muted">#{a.riotId.tagLine}</span>
                  </p>
                  <p className="text-xs text-fg-muted">
                    {a.game.toUpperCase()} · {a.region} · {formatLinkedAt(a.linkedAt)}
                    {a.nickname ? ` · "${a.nickname}"` : ''}
                  </p>
                </div>
                {detailHref && (
                  <Link
                    to={detailHref}
                    aria-label={t('saved.openDetail')}
                    className="text-fg-muted active:opacity-80"
                  >
                    <ChevronRight aria-hidden className="h-5 w-5" />
                  </Link>
                )}
                <button
                  type="button"
                  onClick={() => unlink({ puuid: a.puuid, game: a.game })}
                  disabled={unlinkResult.isLoading}
                  aria-label={t('cta.unsaveAccount')}
                  className="text-fg-muted active:opacity-80 disabled:opacity-50"
                >
                  <Trash2 aria-hidden className="h-4 w-4" />
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {result.data?.disclaimer && (
        <p className="mx-4 text-[10px] leading-relaxed text-fg-muted">
          {result.data.disclaimer}
        </p>
      )}
    </div>
  );
}
